import { useCallback, useEffect, useState } from 'react';

/**
 * Desktop notifications for the things that arrive while you are not looking.
 *
 * Two rules shape this:
 *
 *   Only when the tab is hidden. A pop-up over the screen you are already
 *   reading is noise, and the app has toasts for that. If you can see the app,
 *   the app tells you in the app.
 *
 *   Only after an explicit click. Browsers require a user gesture to prompt,
 *   and asking on page load is both refused by some browsers and resented by
 *   everyone. The prompt lives behind a button in settings.
 *
 * The preference is stored per portal so granting it in the employee app does
 * not silently turn it on in the client portal, which may be a different person
 * on a shared machine.
 */

export type NotificationPermissionState = 'unsupported' | 'default' | 'granted' | 'denied';

export interface DesktopNotificationInput {
  title: string;
  body: string;
  /** Where clicking it should take you, relative to this app's origin. */
  link?: string | null;
  /** Collapses repeats: a second notification with the same tag replaces it. */
  tag?: string;
}

function storageKey(portal: string): string {
  return `gbv:${portal}:desktop-notifications`;
}

export function useDesktopNotifications(portal: string) {
  const supported = typeof window !== 'undefined' && 'Notification' in window;

  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    supported ? (Notification.permission as NotificationPermissionState) : 'unsupported',
  );

  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(storageKey(portal)) === 'on';
  });

  const setPreference = useCallback(
    (next: boolean) => {
      setEnabled(next);
      window.localStorage.setItem(storageKey(portal), next ? 'on' : 'off');
    },
    [portal],
  );

  /** Must be called from a click. Browsers refuse the prompt otherwise. */
  const request = useCallback(async (): Promise<NotificationPermissionState> => {
    if (!supported) return 'unsupported';
    const result = (await Notification.requestPermission()) as NotificationPermissionState;
    setPermission(result);
    // Granting is the whole point of pressing the button, so turn it on too
    // rather than making someone flip a second switch.
    if (result === 'granted') setPreference(true);
    return result;
  }, [supported, setPreference]);

  const notify = useCallback(
    ({ title, body, link, tag }: DesktopNotificationInput) => {
      if (!supported || !enabled || permission !== 'granted') return false;
      // The app is on screen; it can say so itself without hijacking the OS.
      if (document.visibilityState === 'visible') return false;

      try {
        const n = new Notification(title, {
          body,
          tag,
          icon: '/brand/gobeevir-mark.png',
          badge: '/brand/gobeevir-mark.png',
        });
        n.onclick = () => {
          window.focus();
          if (link) window.location.assign(link);
          n.close();
        };
        return true;
      } catch {
        // Some browsers throw here when the page is not in a state they like.
        // A missed notification is not worth breaking the socket handler over.
        return false;
      }
    },
    [supported, enabled, permission],
  );

  // Another tab may have changed the preference.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(portal)) setEnabled(e.newValue === 'on');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [portal]);

  return {
    supported,
    permission,
    /** Granted *and* switched on. Both are required before anything pops up. */
    enabled: supported && permission === 'granted' && enabled,
    request,
    setPreference,
    notify,
  };
}
